import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation, inject, input } from '@angular/core';
import { Animations } from '@fi-shared/utils/animations';
import { FiReportTableFilterBaseComponent } from '@fi-shared/fi-report-table-filter/fi-report-table-filter-base.component';
import {
    IOnboardingReportAvailableItems,
    IOnboardingReportWidgetFilter,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-app/shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import {
    FiOnboardingReportType,
    FiOnboardingReportWidgetType,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import { FiOnboardingReportTableFilterHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/shared/fi-onboarding-report-table-filter-helper.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { WidgetTableFilterMenuType } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter/widget-table-filter-menu-type';
import { IObjectIdName } from '@fi-app/utilities/fi-services/object-types.service';
import { isDefined } from '@uirouter/core';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { IReportAvailableItemsByData } from '@fi-shared/fi-report-table-filter/fi-report-filter-base';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';

@Component({
    selector: 'fi-onboarding-report-table-filter',
    templateUrl: './fi-onboarding-report-table-filter.component.html',
    styleUrl: './fi-onboarding-report-table-filter.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-report-table-filter' },
    standalone: false,
    animations: [Animations.fadeWidth(), Animations.fadeHeight()],
})
export class FiOnboardingReportTableFilterComponent extends FiReportTableFilterBaseComponent<
    IOnboardingReportWidgetFilter,
    IOnboardingReportAvailableItems
> {
    @Input({ required: true }) reportType: FiOnboardingReportType;
    @Input({ required: true }) reportWidgetType: FiOnboardingReportWidgetType;
    @Input({ required: true }) availableItems: IOnboardingReportAvailableItems;

    // for example we have to only users who have access to selected units and onboardings
    @Input({ required: true }) availableItemsByData: IReportAvailableItemsByData;

    public isFilterExpanded = input<boolean>(false);
    public isCustomWidget = input<boolean>(false);
    public isResetButtonVisible = input<boolean>(false);
    private readonly fiOnboardingReportTableFilterHelperService = inject(FiOnboardingReportTableFilterHelperService);
    private readonly fiCustomerDataService = inject(FiCustomerDataService);
    protected readonly FilterTypeLabelSet = { ...this.FilterTypeLabelSet, [FiFilterType.User]: 'form.participants' };

    protected initMenusData(): void {
        this.menusData = this.fiOnboardingReportTableFilterHelperService.getFilterMenusData({
            reportType: this.reportType,
            reportWidgetType: this.reportWidgetType,
            availableItems: this.availableItems,
            availableItemsByDataFn: () => this.availableItemsByData,
        });
    }

    protected isTooltipDisabledFilterMenuAvailable(menuData: ISharingMenuData<INameIdObj>): boolean {
        return menuData.isDisabled && !!this.getFilterMenuDisabledTextByType(menuData);
    }

    protected getFilterMenuDisabledTextByType(menuData: ISharingMenuData<INameIdObj>): string {
        if (menuData.filterType === FiFilterType.Area) {
            if ((this.currentFilter?.BusinessUnitIds?.length ?? 0) > 0) {
                return this.appTextService.get('checklist.filter_disabled_area_by_units_tooltip');
            }
        }

        if (menuData.filterType === FiFilterType.BusinessArea) {
            if ((this.currentFilter?.BusinessUnitIds?.length ?? 0) > 0) {
                return this.appTextService.get('checklist.filter_disabled_business_area_by_units_tooltip');
            }
        }
        return undefined;
    }

    private updateMenusDisabledState(): void {
        this.menusData = this.menusData.map((menuData) => {
            if (menuData.filterType === FiFilterType.Area) {
                menuData.isDisabled = (this.currentFilter?.BusinessUnitIds?.length ?? 0) > 0;
            }
            if (menuData.filterType === FiFilterType.BusinessArea) {
                menuData.isDisabled = (this.currentFilter?.BusinessUnitIds?.length ?? 0) > 0;
            }
            return menuData;
        });
    }

    protected getFilterMenuType(menuData: ISharingMenuData<INameIdObj>): WidgetTableFilterMenuType {
        return this.fiOnboardingReportTableFilterHelperService.getFilterMenuType(
            menuData.filterType,
            this.reportType,
            this.reportWidgetType,
        );
    }

    canDeselectItem(selectedItem: ISelectedFilterItem): boolean {
        return this.fiOnboardingReportTableFilterHelperService.canDeselectItem(
            selectedItem.filterType,
            this.reportWidgetType,
        );
    }

    protected isOptionVisibleFn = (menuData: ISharingMenuData<INameIdObj>, item: INameIdObj): boolean => {
        if (!isDefined(menuData.isOptionHidden)) return true;
        return !menuData.isOptionHidden(item);
    };

    protected setSelectedFilterItems(): void {
        this.selectedFilterItems = this.getSelectedFilterItemsFromFilter(this.currentFilter);
        this.updateMenusDisabledState();
    }
    protected setSelectedMenuItems(): void {
        this.fiOnboardingReportTableFilterHelperService.updatedMenusDataWithSelectedItems(
            this.menusData,
            this.selectedFilterItemsGroups,
        );
    }
    protected getFilterFromSelectedItems(
        selectedFilterItemsGroups: { [key: number]: ISelectedFilterItem[] },
        currentFilter: IOnboardingReportWidgetFilter,
    ): IOnboardingReportWidgetFilter {
        return this.fiOnboardingReportTableFilterHelperService.getFilterFromSelectedItems(
            selectedFilterItemsGroups,
            currentFilter,
        );
    }
    protected getSelectedFilterItemsFromFilter(filter: IOnboardingReportWidgetFilter): ISelectedFilterItem[] {
        return this.fiOnboardingReportTableFilterHelperService.getSelectedFilterItemsFromFilter(
            filter,
            this.availableItems,
        );
    }
    protected onSingleItemSelected(item: INameIdObj, menuData: ISharingMenuData<IObjectIdName>) {
        menuData.selectedItemIds = [item.Id];
        this.cdr.detectChanges();
        this.sharingWithItemChange(menuData.filterType, menuData.selectedItemIds);
    }

    protected menuDataMainItemsSelectionChangeEqualChildrenType(menuData: ISharingMenuData<INameIdObj>) {
        this.sharingWithItemChange(menuData.filterType, [
            ...new Set<number>([...menuData.selectedItemIds, ...menuData.selectedChildItemIds]),
        ]);
        //this.onSelectedItemsChanged();
    }

    protected menuDataMainChildrenSelectionChangeEqualChildrenType(menuData: ISharingMenuData<INameIdObj>) {
        this.sharingWithItemChange(menuData.filterType, [
            ...new Set<number>([...menuData.selectedItemIds, ...menuData.selectedChildItemIds]),
        ]);
        /*const updateMenu: ISharingMenuData<INameIdObj> = this.widgetTableFilterHelperService.getMenuDataByFilterType(
            this.menusData,
            filterType,
            false,
        );
        updateMenu.selectedItemIds = menuData.selectedItemIds;
        updateMenu.selectedChildItemIds = menuData.selectedChildItemIds;
        this.updateMenuSelectedItems(updateMenu);
        return;*/
    }

    protected selectFilter(filterGuid: string): void {
        this.onSelectFilter.emit(filterGuid);
    }

    protected isUpdateButtonVisible(): boolean {
        return this.isFilterChanged && this.currentFilter.Guid?.length && !this.isCustomWidget();
    }

    protected updateCurrentFilter(): void {
        this.onUpdateCurrentFilter.emit();
    }

    protected showDebugInfo(): boolean {
        return false;
        return this.fiCustomerDataService.isTestCustomer();
    }

    public getSelectedItemsCount(): number {
        return Object.values(this.selectedFilterItemsGroups).reduce((acc, group) => acc + group.length, 0) || 0;
    }

    protected readonly WidgetTableFilterMenuType = WidgetTableFilterMenuType;
    protected readonly FiOnboardingReportWidgetType = FiOnboardingReportWidgetType;
    protected readonly FiInfoIconMode = FiInfoIconMode;
}
